import React from "react"
import PropTypes from "prop-types"

import LoaderSection from '../app_components/LoaderSection'
import SettingsTextInput from '../form_sections/SettingsTextInput'
import SettingsLongTextInput from '../form_sections/SettingsLongTextInput'
import DateInput from '../form_sections/DateInput'
import LocationSettings from './LocationSettings'
import GenderSection from './sections/GenderSection'
import NationalitySection from './sections/NationalitySection'
import ProfilePictureSection from './sections/ProfilePictureSection'
import PauseOrCancelAccount from './pause_account/PauseOrCancelAccount'
import Icon from '../app_components/Icon'
import StandardButton from '../app_components/StandardButton'
import { semantic } from '@lexody/design-tokens'
import { getMaxBirthdayDate } from '../../utils/birthdayHelpers'
import { isMissingAvatar } from '../connect_sections/connectMemberHelpers'

function getEditProfileCompletion(user) {
  const checks = [
    Boolean(user.first_name && user.first_name.trim()),
    Boolean(user.last_name && user.last_name.trim()),
    Boolean(user.email && user.email.trim()),
    Boolean(user.bio && user.bio.trim()),
    Boolean(user.gender_id),
    Boolean(user.nationality),
    Boolean(user.birthday),
    Boolean(user.avatar_url && !isMissingAvatar(user.avatar_url)),
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function SettingsSectionCard({ title, subtitle, children }) {
  return (
    <section className="lex-dash-card">
      <header className="lex-dash-card__header">
        <h2 className="lex-dash-card__title">{title}</h2>
        {subtitle && <p className="lex-dash-card__subtitle">{subtitle}</p>}
      </header>
      <div className="lex-dash-card__body">
        {children}
      </div>
    </section>
  )
}

class UserSettingsContainer extends React.Component {

    constructor(props) {
      super(props)

      this.state = {
        user: {},
        loaded: false,
        saveUserTimeout: null,
        savedAlert: null,
        saved: false,
        pauseAccountOpen: false
      }

      this.getUserData = this.getUserData.bind(this)
      this.updateUser = this.updateUser.bind(this)
      this.savedTimer = this.savedTimer.bind(this)
      this.saveUser = this.saveUser.bind(this)
      this.savedAlert = this.savedAlert.bind(this)
      this.updateBirthday = this.updateBirthday.bind(this)
      this.togglePauseContainer = this.togglePauseContainer.bind(this)
    }

    getUserData() {
      $.ajax({
        url: `../getUserData`,
        dataType: 'JSON',
        type: 'GET',
        header: {
          'Content-Type': 'application/json'
        },
        success: (data) => {
          this.setState({
            user: JSON.parse(data.data.current_user),
            loaded: true
          })
        },
        error: function(xhr, status, err) {
          console.log(err)
        }.bind(this)
      });
    }

    componentDidMount() {
      this.getUserData()
    }

    updateBirthday(date) {
      this.updateUser(date, 'birthday')
    }

    updateUser(value, attribute) {
      this.setState({
        user: {...this.state.user, [attribute]: value},
      }, () => {
        const { saveUserTimeout } = this.state;
        clearTimeout(saveUserTimeout);
        this.setState({
          saveUserTimeout: setTimeout(() => {
            this.saveUser()
          }, 300)
        })
      })
    }

    saveUser() {
      let data = {
        user: {
          first_name: this.state.user.first_name,
          last_name: this.state.user.last_name,
          nickname: this.state.user.nickname,
          email: this.state.user.email,
          bio: this.state.user.bio,
          gender_id: this.state.user.gender_id,
          bio: this.state.user.bio,
          birthday: this.state.user.birthday,
          nationality: this.state.user.nationality,
        }
      }
      $.ajax({
        url: `../updateUser`,
        dataType: 'JSON',
        type: 'POST',
        data: data,
        header: {
          'Content-Type': 'application/json'
        },
        success: (data) => {
          this.setState({
            saved: true
          }, () => this.savedTimer())
        },
        error: function(xhr, status, err) {
          console.log(err)
        }.bind(this)
      });
    }

    togglePauseContainer() {
      this.setState({
        pauseAccountOpen: !this.state.pauseAccountOpen
      })
    }

    savedTimer() {
      const { saveUserTimeout } = this.state;
      clearTimeout(saveUserTimeout);
      this.setState({
        saveUserTimeout: setTimeout(() => {
          this.setState({
            saved: false
          })
        }, 1000)
      })
    }

    componentWillUnmount() {
      const { saveUserTimeout } = this.state;
      clearTimeout(saveUserTimeout);
    }

    savedAlert() {
      return(
        <div className="lex-form-toast" role="status">
          <Icon
            name="check-circle"
            size={20}
            color={semantic.success.text.label}
            solid
          />
          <h3>Profile updated!</h3>
        </div>
      )
    }

    render() {
      if(!this.state.loaded) {
        return <LoaderSection />
      }
      if(this.state.pauseAccountOpen) {
        return(
          <PauseOrCancelAccount
            togglePauseContainer={() => this.togglePauseContainer()}
          />
        )
      }
      const displayName = this.state.user.nickname || this.state.user.first_name || 'Your profile';
      const profilePct = getEditProfileCompletion(this.state.user);

      return(
        <div className="lex-form-page">
          {this.state.saved && this.savedAlert()}

          <header className="lex-form-page__header">
            <h1 className="lex-form-page__title">Your Profile</h1>
            <p className="lex-form-page__subtitle">Fill in your explorer card — everything saves as you type.</p>
          </header>

          <section className="lex-dash-hero">
            <ProfilePictureSection user={this.state.user} />
            <div className="lex-dash-hero__body">
              <h2 className="lex-dash-hero__name">{displayName}</h2>
              <p className="lex-dash-hero__meta">Explorer · {profilePct}% complete</p>
              <div className="lex-dash-progress" aria-hidden="true">
                <div className="lex-dash-progress__fill" style={{ width: `${profilePct}%` }} />
              </div>
              <div className="lex-dash-progress__label">
                <span>Profile strength</span>
                <strong>{profilePct}%</strong>
              </div>
            </div>
          </section>

          <div className="lex-settings-cards-grid">
            <SettingsSectionCard
              title="Basic Info"
              subtitle="Your name and how Lexers find you"
            >
              <div className="lex-dash-card__row">
                <SettingsTextInput
                  label="First Name"
                  attribute="first_name"
                  value={this.state.user.first_name}
                  onChange={this.updateUser}
                  placeholder="First name"
                  icon="user"
                  tone="purple"
                />
                <SettingsTextInput
                  label="Preferred Name"
                  attribute="nickname"
                  value={this.state.user.nickname}
                  onChange={this.updateUser}
                  placeholder="Nickname (optional)"
                  icon="star"
                  tone="orange"
                />
              </div>
              <div className="lex-dash-card__row">
                <SettingsTextInput
                  label="Last Name"
                  attribute="last_name"
                  value={this.state.user.last_name}
                  onChange={this.updateUser}
                  placeholder="Last name"
                  icon="user"
                  tone="purple"
                />
                <SettingsTextInput
                  label="Email"
                  attribute="email"
                  value={this.state.user.email}
                  onChange={this.updateUser}
                  placeholder="Email"
                  icon="at"
                  tone="teal"
                />
              </div>
              <SettingsLongTextInput
                label="About you"
                attribute="bio"
                value={this.state.user.bio}
                onChange={this.updateUser}
                placeholder="Why are you learning a language? What do you like to do for fun?"
                tone="coral"
              />
            </SettingsSectionCard>

            <SettingsSectionCard
              title="About You"
              subtitle="Helps us match you with compatible partners"
            >
              <GenderSection
                user={this.state.user}
                updateUser={this.updateUser}
              />
              <NationalitySection
                user={this.state.user}
                updateUser={this.updateUser}
              />
              <DateInput
                onChange={this.updateBirthday}
                label="Birthday"
                value={this.state.user.birthday ? new Date(this.state.user.birthday) : null}
                inputError=""
                maxDate={getMaxBirthdayDate()}
                showYearDropdown={true}
                showMonthDropdown={true}
                tone="teal"
                hint="Must be 18 or older"
              />
            </SettingsSectionCard>

            <SettingsSectionCard
              title="Location"
              subtitle="Find language partners near you"
            >
              <LocationSettings />
            </SettingsSectionCard>
          </div>

          <div className="lex-form-actions">
            <StandardButton
              text="Pause or cancel account"
              alert
              fullWidth
              onClick={() => this.togglePauseContainer()}
            />
          </div>
        </div>
      )
    }
}

export default UserSettingsContainer
